import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { FORM_SCHEMAS } from './constants';
import { getFormFields, getInitFormState } from './helpers';

import FormInput from '../../blaze-plugins/shopware-fe-kp/components/FormInput';
import { validateForm } from '../../blaze-plugins/shopware-fe-kp/components/helpers';
import { GET_COUNTRIES } from '../../blaze-plugins/shopware-fe-kp/services/querys';
import buildSelectOptions from '../../blaze-plugins/shopware-fe-kp/components/helpers/build-select-options';

import { CONTACT_MUTATION } from '../../services';

const ContactForm = ({ children, privacyPolicyUrl, formType = 'default' }) => {
  const router = useRouter();
  const formRef = useRef(null);

  const activeSchema = useMemo(() => FORM_SCHEMAS[formType] || FORM_SCHEMAS.default, [formType]);

  const initialFormState = useMemo(() => getInitFormState(activeSchema), [activeSchema]);

  const [formValues, setFormValues] = useState(initialFormState);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showSuccessMessage, setShowSuccesMessage] = useState(false);

  useEffect(() => {
    setFormValues(initialFormState);
    setHasSubmitted(false);
    setErrorMessage(null);
  }, [initialFormState]);

  const { data: { countries = [] } = {} } = useQuery(GET_COUNTRIES);
  const parsedCountries = useMemo(() => buildSelectOptions(countries), [countries]);

  const [createContact, { loading }] = useMutation(CONTACT_MUTATION);

  const handleValueChange = (key, value) => {
    let parsedValue = value;

    if (typeof value === 'object' && value?.checked !== undefined) {
      parsedValue = value.checked;
    }

    setFormValues(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: parsedValue,
        error: false
      }
    }));
  };

  const scrollToError = () => {
    setTimeout(() => {
      const firstError = formRef.current?.querySelector('.error');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstError?.focus();
    }, 100);
  };

  const handleSubmit = e => {
    e.preventDefault();

    const [isValid, validatedForm] = validateForm(formValues, activeSchema);

    if (!isValid) {
      setFormValues(validatedForm);
      scrollToError();
      return;
    }

    const input = Object.entries(validatedForm).reduce((acc, [key, field]) => {
      const { value } = field;
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      const isEmpty =
        normalizedValue === '' || normalizedValue === null || normalizedValue === undefined;

      if (!field.isRequired && isEmpty) {
        return acc;
      }

      return {
        ...acc,
        [key]: value
      };
    }, {});

    createContact({
      variables: {
        input: {
          ...input,
          ...(input.newsletterSignup !== undefined && {
            newsletterSignup: !!input.newsletterSignup
          }),
          urlSentFrom: router.asPath,
          formType
        }
      },
      onCompleted: () => {
        window?.dataLayer?.push({
          event: 'formSubmission',
          formType,
          formData: validatedForm
        });
        setHasSubmitted(true);
        setErrorMessage(null);
        setShowSuccesMessage(true);
      },
      onError: err => setErrorMessage(err.message)
    });
  };

  const fields = getFormFields(activeSchema);

  if (hasSubmitted) {
    return <div className="contact-form">{children}</div>;
  }

  return (
    <div className="contact-form-container">
      <form
        ref={formRef}
        className="contact-form-container__form"
        onSubmit={handleSubmit}
        noValidate>
        {fields.map(field => {
          if (!field.showInForm) return null;
          if (!formValues[field.fieldId]) return null;

          if (field.fieldId === 'country') {
            return (
              <FormInput
                key={field.fieldId}
                id={field.fieldId}
                className={field.fieldId}
                label={field.label}
                type="select"
                options={parsedCountries}
                data={formValues[field.fieldId]}
                isRequired={field.isRequired}
                onChange={handleValueChange}
              />
            );
          }

          if (field.fieldId === 'newsletterSignup') {
            return (
              <React.Fragment key={field.fieldId}>
                <h3>Newsletter</h3>
                <FormInput
                  id={field.fieldId}
                  className="contact-form-container__form-checkbox"
                  label={
                    <>
                      We will occasionally email you with news about our other products and
                      services, special offers, exclusive content, and events which, based on your
                      dealings with us, we feel may be of interest to you. If you do not wish to
                      receive this, please tick this box.
                    </>
                  }
                  type="checkbox"
                  data={formValues[field.fieldId]}
                  isRequired={field.isRequired}
                  onChange={handleValueChange}
                />
                <p>
                  If you do not tick the box, you can still change your mind at any time by
                  selecting the &quot;unsubscribe&quot; link located on the bottom of any marketing
                  email we send to you. Please see our <a href={privacyPolicyUrl}>Privacy Policy</a>
                </p>
              </React.Fragment>
            );
          }

          return (
            <FormInput
              key={field.fieldId}
              id={field.fieldId}
              className={field.fieldId}
              label={field.label}
              type={field.formFieldType}
              data={formValues[field.fieldId]}
              isRequired={field.isRequired}
              onChange={handleValueChange}
              options={field.options}
            />
          );
        })}

        <button
          type="submit"
          className="contact-form-container__form__submit-button"
          disabled={loading}>
          Submit
        </button>

        {errorMessage && (
          <div className="contact-form-container__form__error-message">
            <span>{errorMessage}</span>
          </div>
        )}
      </form>

      {showSuccessMessage && children}
    </div>
  );
};

export default ContactForm;
